import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Github } from "lucide-react";
import { EASE } from "@/lib/motion";

const frame = {
  hidden: { clipPath: "inset(10% 10% 10% 10%)" },
  show: { clipPath: "inset(0% 0% 0% 0%)", transition: { duration: 1.4, ease: EASE } },
};
const img = { hidden: { scale: 1.25 }, show: { scale: 1, transition: { duration: 1.6, ease: EASE } } };
const meta = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, delay: 0.25, ease: EASE } } };

export const ProjectCard = ({ project, aspect = "aspect-[4/3]", className = "" }) => (
  <motion.article
    className={`group ${className}`}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.15 }}
    data-testid={`project-card-${project.slug}`}
  >
    <Link to={`/projects/${project.slug}`} className="relative block" aria-label={`${project.title} — view project`}>
      <motion.div variants={frame} className={`relative overflow-hidden bg-coal ${aspect}`}>
        <motion.div variants={img} className="h-full w-full">
          <img
            src={project.cover}
            alt={`${project.title} cover`}
            loading="lazy"
            className={`h-full w-full ${project.coverClass || "object-cover"} transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]`}
          />
        </motion.div>
        <div className="absolute inset-0 bg-ink/10 transition-colors duration-700 group-hover:bg-transparent" />
        <span className="absolute bottom-5 right-5 flex h-12 w-12 translate-y-3 items-center justify-center rounded-full bg-ivory text-ink opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <ArrowUpRight size={18} />
        </span>
      </motion.div>
    </Link>
    <motion.div variants={meta} className="mt-7">
      <div className="flex items-center justify-between gap-4">
        <span className="eyebrow" data-testid={`project-category-${project.slug}`}>{project.number} — {project.category}</span>
      </div>
      <h3 className="mt-4 font-serif text-4xl font-light text-ivory sm:text-5xl">{project.title}</h3>
      <p className="mt-4 max-w-xl text-ash">{project.summary}</p>
      <ul className="mt-6 flex flex-wrap gap-2">
        {project.tags.map((t) => (
          <li key={t} className="border border-line px-3 py-1.5 font-mono text-xs text-ash">{t}</li>
        ))}
      </ul>
      <div className="mt-7 flex flex-wrap items-center gap-8">
        <Link to={`/projects/${project.slug}`} className="link-line flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ivory hover:text-gold" data-testid={`project-view-${project.slug}`}>
          View Project <ArrowUpRight size={14} />
        </Link>
        {project.links.live && (
          <a href={project.links.live} target="_blank" rel="noopener noreferrer" className="link-line flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-light" data-testid={`project-live-${project.slug}`}>
            Visit Site <ArrowUpRight size={14} />
          </a>
        )}
        {project.links.source && (
          <a href={project.links.source} target="_blank" rel="noopener noreferrer" className="link-line flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ash hover:text-ivory" data-testid={`project-source-${project.slug}`}>
            <Github size={14} /> View Source
          </a>
        )}
      </div>
    </motion.div>
  </motion.article>
);
